import {
  CloseOutlined,
  LockOpenOutlined,
  Notes,
  NotesOutlined,
  StickyNote2Outlined,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InputField from "components/Input/InputField";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { addMonths, format, parseISO } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTasksByClient } from "utils/taskService";
import { canRequestCreditAdjustment, isSuperAdmin } from "utils/permissionUtils";
import { useStudentInstallments, useAdmissionTerms } from "../admissionApi";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";
import { addDays } from "date-fns";
import { formatDisplayDate } from "utils/dateFormat";
import NotesModal from "components/Modals/NotesModal";
import TermPaymentsModal from "./TermPaymentsModal";
import CreditAdjustmentDialog from "./CreditAdjustmentDialog";
import { savedTermAmounts } from "./sentTermAmounts";
import {
  cancelTermRowUnlock,
  isTermRowUnlocked,
  scheduleFieldsOf,
  unlockTermRow,
} from "./superAdminTermEdit";
import { termCreditAvailable } from "./creditAdjustmentUtils";
import ForceDeleteTermDialog from "views/admin/Accounts/ForceDeleteTermDialog";
import RemoveTermRowDialog from "./RemoveTermRowDialog";
import { ManageAdmissionContextPanel } from "../manageAdmissionContext";
import {
  backendTermHasMoney,
  bindRowsToBackendTerms,
  formatAudCompact,
  isRefundEntry,
  normalizePaymentsForRow,
  sumPaymentsByStatus,
  sumRowPayments,
} from "./termPaymentUtils";

export { sumRowPayments } from "./termPaymentUtils";

export const createTermRow = () => ({
  id: crypto.randomUUID(),
  date: "",
  censusDate: "",
  units: "",
  termFee: "",
  termFeeManual: false,
  materialFee: 0,
  discount: "",
  discountManual: false,
  paidAmount: "",
  payments: [],
});

export const normalizeTermSchedule = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.map((row) => {
    const payments = normalizePaymentsForRow(row);
    const paymentTotal = sumRowPayments(payments);
    return {
      id: row.id || crypto.randomUUID(),
      date: row.date ?? row.admissionDate ?? "",
      censusDate: row?.censusDate
        ? formatDateForInput(row.censusDate)
        : formatDateForInput(addMonths(parseISO(row.date), 1)),
      termFeeManual: Boolean(row.termFeeManual),
      termFee: row.termFee ?? row.perSemFee ?? row.termsFee ?? "",
      materialFee: row.materialFee ?? "",
      discountManual: Boolean(row.discountManual),
      discount: row.discount ?? "",
      paidAmount:
        payments.length > 0 ? String(parseFloat(paymentTotal.toFixed(2))) : row.paidAmount ?? "",
      payments,
      units: row.units ?? "",
      ...(isTermRowUnlocked(row) ? { superAdminEdit: true } : {}),
    };
  });
};

export const syncTermScheduleToCount = (raw, existingSchedule) => {
  if (raw === "" || raw == null) return [];
  const count = parseInt(String(raw), 10);
  if (Number.isNaN(count) || count < 1) return [];
  const schedule = [...normalizeTermSchedule(existingSchedule)].slice(0, count);
  while (schedule.length < count) schedule.push(createTermRow());
  return schedule;
};

const computeTermFee = (row, generalInfo) => {
  const units = Number(row?.units) || 0;
  const fee = Number(generalInfo?.agreedFeePerUnit) || 0;
  return units * fee;
};

const computeTermDiscount = (row, generalInfo) => {
  const units = Number(row?.units) || 0;
  const discountPerUnit = Number(generalInfo?.agreedDiscountPerUnit) || 0;
  return units * discountPerUnit;
};

const AMOUNT_MODES = {
  termFee: { flag: "termFeeManual", compute: computeTermFee },
  discount: { flag: "discountManual", compute: computeTermDiscount },
};

/**
 * Flips a term amount between AUTO and MANUAL, starting from the AUTO value either way. The amount
 * stored on an AUTO row is a stale snapshot nothing refreshes, so seeding MANUAL from it would show
 * a number the user never saw on screen.
 */
export const toggleTermAmountMode = (row, generalInfo, field) => {
  const { flag, compute } = AMOUNT_MODES[field];
  return { ...row, [flag]: !row[flag], [field]: String(compute(row, generalInfo)) };
};

const resolveTermFeeValue = (row, generalInfo) =>
  row.termFeeManual ? Number(row.termFee) || 0 : computeTermFee(row, generalInfo);

const resolveTermDiscountValue = (row, generalInfo) =>
  row.discountManual ? Number(row.discount) || 0 : computeTermDiscount(row, generalInfo);

const computeAgreedAmount = (row, generalInfo) =>
  resolveTermFeeValue(row, generalInfo) +
  Number(row?.materialFee) -
  Number(resolveTermDiscountValue(row, generalInfo));

/**
 * What the student still owes for this term. A withdrawal overrides the schedule: a settled term is
 * worth what it was settled at and a cancelled one is worth nothing, so a screen that kept showing
 * the scheduled fee would chase money the settlement already wrote off. Mirrors the account side.
 */
export const resolveAgreedAmount = (row, generalInfo, backendTerm) => {
  if (backendTerm?.withdrawalState === "CANCELLED") return 0;
  if (backendTerm?.withdrawalState === "SETTLEMENT") return Number(backendTerm.settledAgreed) || 0;
  return computeAgreedAmount(row, generalInfo);
};

const SAVED_AMOUNT_TOOLTIP =
  "Sent to the provider, so this is the saved amount. Schedule changes no longer reach it.";

const AmountModeBadge = ({ manual, saved }) => {
  const label = saved ? "SENT" : manual ? "MANUAL" : "AUTO";
  const tone =
    manual && !saved ? "bg-amber-200 text-amber-900" : "bg-app-surface-hover text-app-text";
  return (
    <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${tone}`}>
      {label}
    </span>
  );
};

const formatAud = (val) => {
  if (val === "" || val == null) return "—";
  const n = parseFloat(val);
  return Number.isNaN(n) ? String(val) : `AUD ${n.toLocaleString()}`;
};

const buildTermTaskDefaults = (row, termIndex, clientName, admissionId, generalInfo) => {
  const label = `Term T${termIndex + 1}`;
  const resolvedFee = resolveTermFeeValue(row, generalInfo);
  const resolvedDiscount = resolveTermDiscountValue(row, generalInfo);
  const lines = [
    `Term: ${label}`,
    row.date ? `Date: ${formatDateForInput(row.date)}` : null,
    row.censusDate ? `Census date: ${formatDateForInput(row.censusDate)}` : null,
    `Term fee: ${formatAud(resolvedFee)}`,
    `Material fee: ${formatAud(row.materialFee)}`,
    `Discount: ${formatAud(resolvedDiscount)}`,
    admissionId ? `Admission ID: ${admissionId}` : null,
  ].filter(Boolean);

  return {
    specificTask: `${label} — Please verify the amount`,
    deadline: row.date || row.censusDate || null,
    comment: lines.join("\n"),
  };
};

const getTermTasks = (allTasks, clientId, admissionId, termIndex) => {
  if (!allTasks?.length || !clientId) return [];
  const prefix = `Term T${termIndex + 1}`;
  const matchesClient = (t) => t.clientId == clientId || t.client?.id == clientId;
  const matchesModule = (t) =>
    t.module?.toLowerCase() === "admission" ||
    (admissionId && t.comment?.includes(`Admission ID: ${admissionId}`));
  const matchesTerm = (t) => t.specificTask?.startsWith(prefix);
  return allTasks.filter((t) => matchesClient(t) && matchesModule(t) && matchesTerm(t));
};

const getAdmissionTermTask = (allTasks, admissionId, termIndex) => {
  if (!allTasks?.length || admissionId == null) return null;
  return (
    allTasks.find(
      (t) =>
        t.taskType === "ADMISSION_TERM" &&
        // eslint-disable-next-line eqeqeq -- admissionId may be number (state) or string (API response), coerce intentionally
        t.admissionId == admissionId &&
        t.termIndex === termIndex
    ) ?? null
  );
};

const parseTermNum = (val, fallback) => {
  const n = Number(
    String(val ?? "")
      .trim()
      .replace(/^T/i, "")
  );
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const getTermPaidAmount = (installments, termNumber) => {
  const total = (installments || [])
    .filter((inst) => parseTermNum(inst.terms, null) === termNumber)
    .reduce((sum, inst) => sum + Number(inst.paidAmount || 0), 0);
  return total > 0 ? total : null;
};

const getRowPaidTotal = (row, installments, termIndex) => {
  const fromPayments = sumRowPayments(row.payments);
  if ((row.payments || []).length > 0) return fromPayments;
  const fromApi = getTermPaidAmount(installments, termIndex + 1);
  if (fromApi != null) return fromApi;
  return Number(row.paidAmount) || 0;
};

const getRowVerifiedPaidSummary = (row, backendPayments) => {
  if ((row.payments || []).length > 0) {
    return sumPaymentsByStatus(row.payments, backendPayments);
  }
  // row.payments not yet loaded — derive directly from backend payments
  if ((backendPayments || []).length > 0) {
    return sumPaymentsByStatus(backendPayments, backendPayments);
  }
  return { verified: 0, pending: 0 };
};

const STATUS_COLORS = {
  COMPLETED: "success",
  IN_PROGRESS: "info",
  CANCELLED: "error",
  PENDING: "warning",
};

const formatDeadline = (d) => formatDisplayDate(d, "—");

const TermTasksRow = ({ tasks, colSpan }) => (
  <TableRow>
    <TableCell
      colSpan={colSpan}
      sx={{ p: 0, borderBottom: "2px solid", borderColor: "primary.light" }}
    >
      <Box sx={{ px: 3, py: 1.5, bgcolor: "background.default" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}>
                Task
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}>
                Assigned to
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}>
                Assigned by
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}>
                Deadline
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 11 }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell sx={{ fontSize: 12, maxWidth: 220 }}>
                  <span className="truncate block">{task.specificTask || "—"}</span>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{task.assignedTo?.name || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{task.assignedBy?.name || "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {formatDeadline(task.deadline)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={task.status || "—"}
                    size="small"
                    color={STATUS_COLORS[task.status?.toUpperCase()] || "default"}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </TableCell>
  </TableRow>
);

const TermContextBanner = ({ row, termIndex, clientName, admissionId, generalInfo }) => (
  <Box sx={{ px: 3, pt: 0, pb: 1 }}>
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Term T{termIndex + 1} details
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Client: {clientName || "—"}
        {admissionId ? ` · Admission #${admissionId}` : ""}
      </Typography>
      <Box
        component="dl"
        sx={{
          m: 0,
          mt: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
          gap: "4px 16px",
          typography: "body2",
        }}
      >
        <Typography component="dt" color="text.secondary">
          Date
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {row.date || "—"}
        </Typography>
        <Typography component="dt" color="text.secondary">
          Census date
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {row.censusDate || "—"}
        </Typography>
        <Typography component="dt" color="text.secondary">
          Term fee
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {formatAud(resolveTermFeeValue(row, generalInfo))}
        </Typography>
        <Typography component="dt" color="text.secondary">
          Material fee
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {formatAud(row.materialFee)}
        </Typography>
        <Typography component="dt" color="text.secondary">
          Discount
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          {formatAud(resolveTermDiscountValue(row, generalInfo))}
        </Typography>
      </Box>
    </Paper>
  </Box>
);

const TermScheduleTable = ({
  generalInfo,
  setGeneralInfo,
  admissionContext = null,
  lockedTermIds = new Set(),
  rejectedTermIndices = new Set(),
  onSaveBeforeTask = null,
  onRefreshLock = null,
  onSaveTermSchedule = null,
  scheduleReadOnly = false,
}) => {
  const [{ clientList }, { refetchClient }] = useKothar();
  const [expandedTerms, setExpandedTerms] = useState(new Set());

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);
  const clientId = generalInfo?.clientId ?? null;
  const admissionId = generalInfo?.id ?? null;
  const { data: allTasks = [] } = useTasksByClient(clientId);
  const { data: installments = [] } = useStudentInstallments(admissionId);
  const cadId = generalInfo?.clientAccountDetailId ?? null;
  const queryClient = useQueryClient();
  const { data: backendTerms = [], refetch: refetchBackendTerms } = useAdmissionTerms(cadId);

  // Schedule rows are joined to their backend admission term by a STABLE payment-uuid binding,
  // never by array position — so deleting/reordering a row never misattributes another term's
  // payments. See bindRowsToBackendTerms. `migrated` flag (CAD LEGACY_MIGRATION marker) comes from
  // the bound term — NOT `imported`, which is also true for genuinely verified new terms.
  const termSchedule = normalizeTermSchedule(generalInfo?.termSchedule);
  const rowBinding = bindRowsToBackendTerms(termSchedule, backendTerms);
  const backendTermForRow = (rowId) => rowBinding.get(rowId) ?? null;

  const termIdForIndex = (terms, termIndex) =>
    terms.find((t) => t.termNumber === termIndex + 1)?.id ?? null;

  // A row is safe to delete only if it — and every row after it, which would shift up — carries no
  // money (no payments, not sent to provider, not claimed). Shifting/removing a money-bearing term
  // would orphan or misattribute real records, so we block it (remove the payments first, super
  // admin, or use term swap). Mirrors the backend safety validation on save.
  const rowOrLaterHasMoney = (index) =>
    termSchedule.slice(index).some((r) => backendTermHasMoney(backendTermForRow(r.id)));

  const ensureTermReadyForPayment = async (rowId, termIndex) => {
    const existing = backendTermForRow(rowId)?.id ?? null;
    if (existing) return existing;

    if (!onSaveTermSchedule) return null;

    const saved = await onSaveTermSchedule();
    if (!saved) return null;

    if (cadId) {
      const cached = queryClient.getQueryData(["admissionTerms", cadId]);
      const fromCache = termIdForIndex(Array.isArray(cached) ? cached : [], termIndex);
      if (fromCache) return fromCache;
    }

    const { data: freshTerms = [] } = await refetchBackendTerms();
    return termIdForIndex(freshTerms, termIndex);
  };

  const mergeBackendPaymentsIntoRow = (rowId) => {
    setGeneralInfo((prev) => {
      const schedule = normalizeTermSchedule(prev.termSchedule);
      const bpForRow = bindRowsToBackendTerms(schedule, backendTerms).get(rowId)?.payments ?? [];
      if (!bpForRow.length) return prev;
      const updated = schedule.map((row) => {
        if (row.id !== rowId) return row;
        const localIds = new Set((row.payments || []).map((p) => p.id));
        const newFromBackend = bpForRow
          .filter((bp) => bp.uuid && !localIds.has(bp.uuid))
          .map((bp) => ({
            id: bp.uuid,
            amount: bp.amount ?? "",
            // Without it a merged refund reads as a payment in the row's own totals.
            entryType: isRefundEntry(bp.entryType) ? "REFUND" : "PAYMENT",
            date: bp.date ?? "",
            reference: bp.reference ?? "",
            notes: bp.notes ?? "",
            attachments: Array.isArray(bp.attachments) ? bp.attachments : [],
            notifyAccounts: Boolean(bp.notifyAccounts),
            createdAt: bp.createdAt ?? null,
          }));
        if (newFromBackend.length === 0) return row;
        return { ...row, payments: [...(row.payments || []), ...newFromBackend] };
      });
      return { ...prev, termSchedule: updated };
    });
  };

  const [notesModal, setNotesModal] = useState({ open: false, termIndex: -1 });
  const [paymentsModal, setPaymentsModal] = useState({
    open: false,
    rowId: null,
    termIndex: 0,
    rowLocked: false,
  });
  const [notesCounts, setNotesCounts] = useState({});

  // Super admins may override the lock on a verified/imported term. Default state stays locked; an
  // explicit unlock (after a warning) marks that single row editable, and the mark travels with the
  // row to every schedule save, where the backend applies the row as shown and audits the change.
  const superAdmin = isSuperAdmin();
  const unlockedFrom = useRef({});
  const [unlockDialog, setUnlockDialog] = useState({ open: false, rowId: null, termLabel: "" });
  // Super-admin "force delete" of a saved term (even with payments): { rowId, termNumber }.
  const [forceDelete, setForceDelete] = useState(null);
  const [creditMove, setCreditMove] = useState(null);
  const mayMoveCredit = canRequestCreditAdjustment();

  const [rowRemoval, setRowRemoval] = useState(null);

  const dropTermRow = (rowId) => {
    setGeneralInfo((prev) => {
      const schedule = normalizeTermSchedule(prev.termSchedule).filter((r) => r.id !== rowId);
      return {
        ...prev,
        term: schedule.length ? String(schedule.length) : "",
        termSchedule: schedule,
      };
    });
  };

  // The server already removed the term and moved every later one up, along with the notes, tasks,
  // installments and locks keyed by position, so everything read by position is re-read.
  const refreshAfterTermRemoval = () => {
    setNotesCounts({});
    refetchBackendTerms();
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["studentInstallments", admissionId] });
    onRefreshLock?.();
  };

  const handleTermRowRemoved = (rowId, removedOnServer) => {
    dropTermRow(rowId);
    if (removedOnServer) refreshAfterTermRemoval();
  };

  const handleTermForceDeleted = (rowId) => {
    dropTermRow(rowId);
    refreshAfterTermRemoval();
  };
  const isSuperAdminUnlocked = (rowId) =>
    rowId != null && isTermRowUnlocked(termSchedule.find((r) => r.id === rowId));
  const enableSuperAdminEdit = (rowId) => {
    const row = termSchedule.find((r) => r.id === rowId);
    if (!row) return;
    unlockedFrom.current = { ...unlockedFrom.current, [rowId]: scheduleFieldsOf(row) };
    setGeneralInfo((prev) => ({
      ...prev,
      termSchedule: unlockTermRow(normalizeTermSchedule(prev.termSchedule), rowId),
    }));
  };
  const cancelSuperAdminEdit = (rowId) => {
    const { [rowId]: original, ...stillUnlocked } = unlockedFrom.current;
    unlockedFrom.current = stillUnlocked;
    setGeneralInfo((prev) => ({
      ...prev,
      termSchedule: cancelTermRowUnlock(normalizeTermSchedule(prev.termSchedule), rowId, original),
    }));
  };

  const fetchNotesCount = async (termIndex) => {
    if (!clientId || admissionId == null) return;
    try {
      const res = await axios.get(
        `${API_URL}/discussion/${clientId}?type=admission_term_${admissionId}_${termIndex}`
      );
      const count = res?.data?.comments?.length ?? 0;
      setNotesCounts((prev) => ({ ...prev, [termIndex]: count }));
    } catch {
      // silently ignore count fetch errors
    }
  };

  useEffect(() => {
    if (!clientId || admissionId == null || termSchedule.length === 0) return;
    termSchedule.forEach((_, idx) => fetchNotesCount(idx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, admissionId, termSchedule.length]);
  const toggleTermExpand = (rowId) =>
    setExpandedTerms((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  const hasLockedTerms = lockedTermIds.size > 0;
  const isRowLocked = (rowId, index) =>
    lockedTermIds.has(rowId) || lockedTermIds.has(String(index + 1));

  const handleTermsCountChange = (e) => {
    console.log("handleTermsCountChange", e.target.value);
    const raw = e.target.value;
    if (raw > 0) {
      if (
        window.confirm(
          "Are you sure you want to change the number of terms? This will reset the term schedule."
        )
      ) {
        setGeneralInfo((prev) => ({
          ...prev,
          term: raw,
          termSchedule: syncTermScheduleToCount(raw, prev.termSchedule),
        }));
      }
    }
  };

  const updateTermRow = (rowId, field, value) => {
    if (field === "censusDate" && value) {
      const row = termSchedule.find((r) => r.id === rowId);
      if (row?.date && value <= row.date) {
        toast.warning("Census date must be after the term date.");
        return;
      }
    }
    if (field === "date" && value) {
      const row = termSchedule.find((r) => r.id === rowId);
      if (row?.censusDate && value >= row.censusDate) {
        toast.warning("Term date must be before the census date.");
      }
    }
    setGeneralInfo((prev) => ({
      ...prev,
      termSchedule: normalizeTermSchedule(prev.termSchedule).map((row) => {
        if (row.id !== rowId) return row;
        if (field === "date") {
          return {
            ...row,
            date: value,
            censusDate: formatDateForInput(addMonths(parseISO(value), 1)),
          };
        }
        if (field === "termFee") {
          return { ...row, termFeeManual: true, termFee: value };
        }
        if (field === "discount") {
          return { ...row, discountManual: true, discount: value };
        }
        return { ...row, [field]: value };
      }),
    }));
  };

  const toggleAmountMode = (rowId, field) => {
    setGeneralInfo((prev) => ({
      ...prev,
      termSchedule: normalizeTermSchedule(prev.termSchedule).map((row) =>
        row.id === rowId ? toggleTermAmountMode(row, prev, field) : row
      ),
    }));
  };

  const setRowPayments = (rowId, payments) => {
    const total = sumRowPayments(payments);
    setGeneralInfo((prev) => ({
      ...prev,
      termSchedule: normalizeTermSchedule(prev.termSchedule).map((row) =>
        row.id === rowId
          ? {
              ...row,
              payments,
              paidAmount: total > 0 ? String(parseFloat(total.toFixed(2))) : "",
            }
          : row
      ),
    }));
  };

  const addTermRow = () => {
    setGeneralInfo((prev) => {
      const schedule = [...normalizeTermSchedule(prev.termSchedule), createTermRow()];
      return { ...prev, term: String(schedule.length), termSchedule: schedule };
    });
  };

  const removeTermRow = (rowId) => {
    const index = termSchedule.findIndex((row) => row.id === rowId);
    if (isRowLocked(rowId, index)) return;
    if (rowOrLaterHasMoney(index)) {
      toast.warning(
        "This term — or a later term that would shift up — has recorded payments or was sent to the provider. Remove those payments first (super admin) or use term swap before deleting."
      );
      return;
    }
    if (admissionId == null) {
      dropTermRow(rowId);
      return;
    }
    setRowRemoval({
      rowId,
      termNumber: index + 1,
      notesCount: notesCounts[index] || 0,
      tasksCount: getTermTasks(allTasks, clientId, admissionId, index).length,
      accountNotesCount: backendTermForRow(rowId)?.notesCount || 0,
      laterTermsCount: termSchedule.length - index - 1,
    });
  };

  const resolveClientName = () => {
    const fromList = clientList?.find((c) => c.id === generalInfo?.clientId);
    return fromList?.name || generalInfo?.name || generalInfo?.clientName || "";
  };

  const clientName = resolveClientName();

  const addNotes = (rowId) => {
    const termIndex = termSchedule.findIndex((r) => r.id === rowId);
    setNotesModal({ open: true, termIndex });
  };

  const closeNotesModal = () => {
    const { termIndex } = notesModal;
    setNotesModal({ open: false, termIndex: -1 });
    if (termIndex >= 0) fetchNotesCount(termIndex);
  };

  return (
    <div className="space-y-5">
      <ManageAdmissionContextPanel context={admissionContext} />
      <InputField
        fullWidth
        label="Number of terms *"
        placeholder="e.g. 3"
        name="term"
        type="number"
        min={1}
        required
        value={
          generalInfo?.term != null && generalInfo?.term !== "" ? String(generalInfo.term) : ""
        }
        onChange={handleTermsCountChange}
        // onInput={handleTermsCountChange}
        disabled={hasLockedTerms || scheduleReadOnly}
      />

      {termSchedule.length > 0 && (
        <Paper variant="outlined" className="border-app-border overflow-hidden ">
          {!generalInfo?.coeReceived && (
            <Alert severity="info" sx={{ m: 2, mb: 0 }}>
              COE fee payments are recorded on Step 5 — Signed offer & COE, and can be paid in
              instalments. Ones sent for verification appear here as Term 1 payments; ones filed as
              a receipt only stay on that step.
            </Alert>
          )}
          <div className="px-4 py-3 border-b border-app-border bg-app-surface-muted/80 flex justify-between items-center">
            <Typography variant="subtitle2" className="font-semibold text-app-text">
              Term schedule ({termSchedule.length})
            </Typography>
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addTermRow}
                disabled={scheduleReadOnly}
              >
                Add row
              </Button>
            </div>
          </div>
          <TableContainer className="overflow-x-auto">
            <Table size="small" className="min-w-full">
              <TableHead>
                <TableRow className="bg-app-surface-muted">
                  <TableCell width={48}>#</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Census date</TableCell>
                  <TableCell>Units</TableCell>
                  <TableCell>Total Term fee</TableCell>
                  <TableCell>Material fee</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Agreed Amount</TableCell>
                  <TableCell align="center">Payments</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Tasks</TableCell>
                  <TableCell align="right" width={80} />
                </TableRow>
              </TableHead>
              <TableBody>
                {termSchedule.map((row, index) => {
                  const migratedRow = backendTermForRow(row.id)?.migrated === true;
                  const deleteBlockedByMoney = rowOrLaterHasMoney(index);
                  const superAdminCanUnlock =
                    !scheduleReadOnly && superAdmin && isRowLocked(row.id, index) && !migratedRow;
                  const verificationLocked =
                    isRowLocked(row.id, index) &&
                    !migratedRow &&
                    !(superAdmin && isSuperAdminUnlocked(row.id));
                  const rowLocked = scheduleReadOnly || verificationLocked;
                  const backendTermNumber = backendTermForRow(row.id)?.termNumber ?? null;
                  const canCascadeDelete =
                    superAdmin &&
                    deleteBlockedByMoney &&
                    termSchedule.length > 1 &&
                    backendTermNumber != null;
                  const deleteIsCascade = deleteBlockedByMoney && canCascadeDelete;
                  const deleteDisabled =
                    rowLocked ||
                    termSchedule.length <= 1 ||
                    (deleteBlockedByMoney && !canCascadeDelete);
                  const handleDeleteClick = () => {
                    if (deleteBlockedByMoney) {
                      if (canCascadeDelete)
                        setForceDelete({ rowId: row.id, termNumber: backendTermNumber });
                      return;
                    }
                    removeTermRow(row.id);
                  };
                  const deleteTooltip = rowLocked
                    ? "This term is paid or invoiced and cannot be changed"
                    : deleteIsCascade
                    ? "Delete this term — removes its payments and all related data, then shifts later terms up. Opens a confirmation. Audited."
                    : deleteBlockedByMoney
                    ? superAdmin
                      ? "Save the schedule first to enable delete for this term."
                      : "This term — or a later term that would shift up — has payments or was sent to the provider. Ask a super admin, or use term swap."
                    : "Remove this term";
                  const termTasks = getTermTasks(allTasks, clientId, admissionId, index);
                  const taskCount = termTasks.length;
                  const isExpanded = expandedTerms.has(row.id);
                  const completedCount = termTasks.filter(
                    (t) => t.status?.toUpperCase() === "COMPLETED"
                  ).length;
                  const backendPaymentsForRow = backendTermForRow(row.id)?.payments ?? [];
                  const paymentCount = Math.max(
                    (row.payments || []).length,
                    backendPaymentsForRow.length
                  );
                  const savedAmounts = savedTermAmounts(backendTermForRow(row.id), {
                    unlocked: isSuperAdminUnlocked(row.id),
                  });
                  const amountsLocked = rowLocked || Boolean(savedAmounts);
                  const agreedAmount = savedAmounts
                    ? savedAmounts.agreed
                    : resolveAgreedAmount(row, generalInfo, backendTermForRow(row.id));
                  const { verified: verifiedPaid, pending: pendingPaid } =
                    getRowVerifiedPaidSummary(row, backendPaymentsForRow);
                  const paidTotal = getRowPaidTotal(row, installments, index); // kept for term data snapshot
                  const dueAmount = Math.max(0, agreedAmount - verifiedPaid);
                  const rowTermId = backendTermForRow(row.id)?.id ?? null;
                  const credit =
                    mayMoveCredit && rowTermId
                      ? termCreditAvailable(backendPaymentsForRow, agreedAmount)
                      : 0;
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow hover>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span>T{index + 1}</span>
                            {migratedRow && (
                              <Tooltip title="Legacy-migrated term — complete the missing units / census. Fields stay editable.">
                                <Chip
                                  label="Migrated"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ fontSize: 9, height: 18 }}
                                />
                              </Tooltip>
                            )}
                            {superAdminCanUnlock &&
                              (isSuperAdminUnlocked(row.id) ? (
                                <>
                                  <Tooltip title="Editing as super admin — changes are audited">
                                    <Chip
                                      label="SA edit"
                                      size="small"
                                      color="warning"
                                      sx={{ fontSize: 9, height: 18 }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Cancel super-admin edit and undo its unsaved changes">
                                    <IconButton
                                      size="small"
                                      onClick={() => cancelSuperAdminEdit(row.id)}
                                    >
                                      <CloseOutlined
                                        sx={{ fontSize: 14 }}
                                        className="text-app-text-muted"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <Tooltip title="Super admin: edit this locked term">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setUnlockDialog({
                                        open: true,
                                        rowId: row.id,
                                        termLabel: `T${index + 1}`,
                                      })
                                    }
                                  >
                                    <LockOpenOutlined
                                      sx={{ fontSize: 16 }}
                                      className="text-orange-400"
                                    />
                                  </IconButton>
                                </Tooltip>
                              ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={formatDateForInput(row.date)}
                            onChange={(e) => updateTermRow(row.id, "date", e.target.value)}
                            disabled={rowLocked}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: "9999-12-31" }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={formatDateForInput(row.censusDate)}
                            onChange={(e) => updateTermRow(row.id, "censusDate", e.target.value)}
                            disabled={rowLocked}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: "9999-12-31" }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={row.units}
                            onChange={(e) => updateTermRow(row.id, "units", e.target.value)}
                            disabled={rowLocked}
                            className="min-w-[70px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              savedAmounts
                                ? SAVED_AMOUNT_TOOLTIP
                                : rowLocked
                                ? "This term is locked"
                                : row.termFeeManual
                                ? "Manual fee (Shift+click to switch back to auto)"
                                : "Auto-calculated (Shift+click to edit manually)"
                            }
                          >
                            <span>
                              <InputField
                                type="number"
                                min={0}
                                size="small"
                                fullWidth
                                className="min-w-[70px]"
                                value={
                                  savedAmounts
                                    ? savedAmounts.termFee
                                    : row.termFeeManual
                                    ? row.termFee
                                    : computeTermFee(row, generalInfo)
                                }
                                outerClassName={
                                  row.termFeeManual && !savedAmounts
                                    ? "border-amber-400 bg-amber-50"
                                    : "border-app-border bg-app-surface-muted"
                                }
                                endIcon={
                                  <AmountModeBadge
                                    manual={row.termFeeManual}
                                    saved={Boolean(savedAmounts)}
                                  />
                                }
                                onClick={(e) => {
                                  if (!amountsLocked && e.shiftKey) {
                                    e.preventDefault();
                                    toggleAmountMode(row.id, "termFee");
                                  }
                                }}
                                onChange={(e) => {
                                  if (!row.termFeeManual) return;
                                  updateTermRow(row.id, "termFee", e.target.value);
                                }}
                                disabled={amountsLocked}
                                InputProps={{ readOnly: !row.termFeeManual }}
                                placeholder={row.termFeeManual ? "Enter fee" : "Auto"}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={savedAmounts ? savedAmounts.materialFee : row.materialFee}
                            onChange={(e) => updateTermRow(row.id, "materialFee", e.target.value)}
                            disabled={amountsLocked}
                            className="min-w-[100px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              savedAmounts
                                ? SAVED_AMOUNT_TOOLTIP
                                : rowLocked
                                ? "This term is locked"
                                : row.discountManual
                                ? "Manual discount (Shift+click to switch back to auto)"
                                : "Auto-calculated (Shift+click to edit manually)"
                            }
                          >
                            <span>
                              <InputField
                                type="number"
                                min={0}
                                size="small"
                                fullWidth
                                className="min-w-[70px]"
                                value={
                                  savedAmounts
                                    ? savedAmounts.discount
                                    : row.discountManual
                                    ? row.discount
                                    : computeTermDiscount(row, generalInfo)
                                }
                                outerClassName={
                                  row.discountManual && !savedAmounts
                                    ? "border-amber-400 bg-amber-50"
                                    : "border-app-border bg-app-surface-muted"
                                }
                                endIcon={
                                  <AmountModeBadge
                                    manual={row.discountManual}
                                    saved={Boolean(savedAmounts)}
                                  />
                                }
                                onClick={(e) => {
                                  if (!amountsLocked && e.shiftKey) {
                                    e.preventDefault();
                                    toggleAmountMode(row.id, "discount");
                                  }
                                }}
                                onChange={(e) => {
                                  if (!row.discountManual) return;
                                  updateTermRow(row.id, "discount", e.target.value);
                                }}
                                disabled={amountsLocked}
                                InputProps={{ readOnly: !row.discountManual }}
                                placeholder={row.discountManual ? "Enter discount" : "Auto"}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            className="min-w-[100px]"
                            value={agreedAmount}
                            disabled
                            InputProps={{ readOnly: true }}
                            sx={{
                              "& .MuiInputBase-input": {
                                bgcolor: "background.default",
                                fontWeight: 600,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              paymentCount > 0
                                ? `${paymentCount} payment(s) — click to view or add`
                                : "Add payments"
                            }
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                mergeBackendPaymentsIntoRow(row.id);
                                setPaymentsModal({
                                  open: true,
                                  rowId: row.id,
                                  termIndex: index,
                                  rowLocked: verificationLocked,
                                });
                              }}
                            >
                              <Badge
                                badgeContent={paymentCount}
                                color="primary"
                                max={99}
                                overlap="circular"
                              >
                                <AddIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ minWidth: 80, fontWeight: 500 }}>
                            {formatAudCompact(verifiedPaid > 0 ? verifiedPaid : null)}
                          </Typography>
                          {pendingPaid > 0 && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              sx={{ display: "block", lineHeight: 1.2 }}
                            >
                              +{formatAudCompact(pendingPaid)} pending
                            </Typography>
                          )}
                          {credit > 0 && (
                            <Tooltip title="Paid more than agreed. Click to move some of it to another term of this admission.">
                              <Chip
                                label={`Credit ${formatAudCompact(credit)}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={() =>
                                  setCreditMove({ termId: rowTermId, termLabel: `T${index + 1}` })
                                }
                                sx={{ fontSize: 10, height: 18, mt: 0.5 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 80, fontWeight: 500 }}
                            color={dueAmount > 0 ? "warning.main" : "success.main"}
                          >
                            {formatAudCompact(dueAmount > 0 ? dueAmount : 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {taskCount > 0 ? (
                            <Tooltip title={`${completedCount}/${taskCount} completed`}>
                              <Chip
                                label={`${completedCount}/${taskCount}`}
                                size="small"
                                color={completedCount === taskCount ? "success" : "warning"}
                                onClick={() => toggleTermExpand(row.id)}
                                sx={{ cursor: "pointer", fontSize: 11 }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <div className="flex items-center justify-end">
                            {taskCount > 0 && (
                              <Tooltip title={isExpanded ? "Hide tasks" : "Show tasks"}>
                                <IconButton size="small" onClick={() => toggleTermExpand(row.id)}>
                                  {isExpanded ? (
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                notesCounts[index] ? `${notesCounts[index]} note(s)` : "Add Notes"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => addNotes(row.id)}
                                  disabled={rowLocked}
                                >
                                  <Badge
                                    badgeContent={notesCounts[index] || 0}
                                    color="error"
                                    max={99}
                                    sx={{
                                      "& .MuiBadge-badge": {
                                        fontSize: 9,
                                        minWidth: 14,
                                        height: 14,
                                      },
                                    }}
                                  >
                                    <StickyNote2Outlined fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={deleteTooltip}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleDeleteClick}
                                  disabled={deleteDisabled}
                                >
                                  {deleteIsCascade ? (
                                    <DeleteForeverOutlinedIcon fontSize="small" />
                                  ) : (
                                    <DeleteOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && <TermTasksRow tasks={termTasks} colSpan={11} />}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {notesModal.open && (
        <NotesModal
          open={notesModal.open}
          handleClose={closeNotesModal}
          attachmentsEnabled={true}
          studentId={generalInfo?.clientId}
          type={`admission_term_${admissionId}_${notesModal.termIndex}`}
        />
      )}

      <RemoveTermRowDialog
        admissionId={admissionId}
        target={rowRemoval}
        onClose={() => setRowRemoval(null)}
        onRemoved={handleTermRowRemoved}
      />

      <ForceDeleteTermDialog
        open={Boolean(forceDelete)}
        onClose={() => setForceDelete(null)}
        cadId={cadId}
        termNumber={forceDelete?.termNumber}
        onDeleted={() => forceDelete && handleTermForceDeleted(forceDelete.rowId)}
      />

      {paymentsModal.open &&
        paymentsModal.rowId &&
        (() => {
          const paymentsRow = termSchedule.find((r) => r.id === paymentsModal.rowId);
          const termIdx = paymentsModal.termIndex;
          const modalSavedAmounts = savedTermAmounts(backendTermForRow(paymentsModal.rowId), {
            unlocked: isSuperAdminUnlocked(paymentsModal.rowId),
          });
          const agreed = modalSavedAmounts
            ? modalSavedAmounts.agreed
            : resolveAgreedAmount(
                paymentsRow ?? {},
                generalInfo,
                backendTermForRow(paymentsModal.rowId)
              );

          return (
            <TermPaymentsModal
              open={paymentsModal.open}
              onClose={() =>
                setPaymentsModal({ open: false, rowId: null, termIndex: 0, rowLocked: false })
              }
              termLabel={`T${termIdx + 1}`}
              clientName={clientName}
              agreedAmount={agreed}
              payments={paymentsRow?.payments ?? []}
              readOnly={paymentsModal.rowLocked}
              onChangePayments={(payments) => setRowPayments(paymentsModal.rowId, payments)}
              termId={backendTermForRow(paymentsModal.rowId)?.id ?? null}
              backendPayments={backendTermForRow(paymentsModal.rowId)?.payments ?? []}
              admissionId={admissionId}
              termIndex={termIdx}
              coeReceived={Boolean(generalInfo?.coeReceived)}
              coeFeePaymentUuid={generalInfo?.coeFeeTerm1PaymentUuid ?? null}
              onPaymentsSynced={() => mergeBackendPaymentsIntoRow(paymentsModal.rowId)}
              ensureTermReady={() => ensureTermReadyForPayment(paymentsModal.rowId, termIdx)}
            />
          );
        })()}

      {creditMove && (
        <CreditAdjustmentDialog
          open
          onClose={() => setCreditMove(null)}
          termId={creditMove.termId}
          termLabel={creditMove.termLabel}
        />
      )}

      <Dialog
        open={unlockDialog.open}
        onClose={() => setUnlockDialog({ open: false, rowId: null, termLabel: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit locked term {unlockDialog.termLabel}?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            You are overriding a verified / locked term as a super admin. Manage Admissions is the
            source of truth — saving will propagate schedule fields (fee, material fee, discount,
            units, dates) to:
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              <li>Direct Account installment rows and client Accounts tab</li>
              <li>Verification history snapshots for the edited term(s)</li>
              <li>Provider account terms already sent to the provider</li>
              <li>
                Linked provider invoice commission/bonus lines (including paid invoices), using the
                current commission and bonus amounts on the term
              </li>
              <li>Verification audit log (who, when, and what changed)</li>
            </ul>
            Provider invoices bill commission/bonus — changing fee or discount alone does not
            recalculate commission unless those amounts are also updated on the term. Review
            provider invoices after saving. Client term payments are not moved automatically; to fix
            a payment on the wrong term, delete it and re-add it on the correct term.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, rowId: null, termLabel: "" })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (unlockDialog.rowId != null) enableSuperAdminEdit(unlockDialog.rowId);
              setUnlockDialog({ open: false, rowId: null, termLabel: "" });
            }}
          >
            I understand — enable editing
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

function DiscountCells({ row, rowLocked, onUpdateRow }) {
  const termFee = parseFloat(row.termFee) || 0;
  const discount = parseFloat(row.discount) || 0;
  const pctFocused = useRef(false);

  const derivePct = (aud, fee) =>
    fee > 0 ? String(parseFloat(((aud / fee) * 100).toFixed(2))) : "";

  const [pctInput, setPctInput] = useState(() => derivePct(discount, termFee));

  useEffect(() => {
    if (!pctFocused.current) {
      setPctInput(derivePct(parseFloat(row.discount) || 0, parseFloat(row.termFee) || 0));
    }
  }, [row.discount, row.termFee]);

  const handleAudChange = (e) => {
    onUpdateRow(row.id, "discount", e.target.value);
  };

  const handlePctChange = (e) => {
    const val = e.target.value;
    setPctInput(val);
    const pct = parseFloat(val);
    if (!isNaN(pct) && termFee > 0) {
      onUpdateRow(row.id, "discount", String(parseFloat(((pct / 100) * termFee).toFixed(2))));
    } else if (val === "" || val === "0") {
      onUpdateRow(row.id, "discount", "0");
    }
  };

  return (
    <>
      <TableCell>
        <TextField
          type="number"
          size="small"
          fullWidth
          value={row.discount}
          onChange={handleAudChange}
          disabled={rowLocked}
        />
      </TableCell>
      {/* <TableCell>
        <TextField
          type="number"
          size="small"
          fullWidth
          value={pctInput}
          onChange={handlePctChange}
          onFocus={() => {
            pctFocused.current = true;
          }}
          onBlur={() => {
            pctFocused.current = false;
          }}
          disabled={rowLocked || !termFee}
          placeholder={termFee ? "%" : "Set fee first"}
          inputProps={{ min: 0, max: 100, step: 0.01 }}
        />
      </TableCell> */}
    </>
  );
}

export default TermScheduleTable;
